import { apiFetch } from './client';
import type {
  AdminStats,
  AppSettings,
  Bonuses,
  Club,
  EventComment,
  EventReview,
  HistoryItem,
  Medal,
  Category,
  EventCard,
  EventDetail,
  Participant,
  PersonCard,
  PersonDetail,
  Registration,
  RegistrationStatus,
  StatsPeriod,
} from './types';

export type Period = 'today' | 'week' | 'upcoming' | 'past';

export const api = {
  categories: () => apiFetch<Category[]>('/api/v1/categories'),

  events: (params: { category?: string; period?: Period } = {}) => {
    const search = new URLSearchParams();
    if (params.category) search.set('category', params.category);
    if (params.period) search.set('period', params.period);
    const qs = search.toString();
    return apiFetch<EventCard[]>(`/api/v1/events${qs ? `?${qs}` : ''}`);
  },

  event: (id: number) => apiFetch<EventDetail>(`/api/v1/events/${id}`),

  join: (id: number, useBonuses = false) =>
    apiFetch<Registration>(
      `/api/v1/events/${id}/join${useBonuses ? '?use_bonuses=true' : ''}`,
      { method: 'POST' },
    ),

  comments: (eventId: number) =>
    apiFetch<EventComment[]>(`/api/v1/events/${eventId}/comments`),

  addComment: (eventId: number, body: string) =>
    apiFetch<EventComment>(`/api/v1/events/${eventId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  deleteComment: (eventId: number, commentId: number) =>
    apiFetch<{ status: string }>(
      `/api/v1/events/${eventId}/comments/${commentId}`,
      { method: 'DELETE' },
    ),

  reviews: (eventId: number) =>
    apiFetch<EventReview[]>(`/api/v1/events/${eventId}/reviews`),

  addReview: (eventId: number, body: { rating: number; body?: string | null; is_public?: boolean }) =>
    apiFetch<Medal[]>(`/api/v1/events/${eventId}/reviews`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  medals: () => apiFetch<Medal[]>('/api/v1/medals'),

  medalsOf: (userId: number) => apiFetch<Medal[]>(`/api/v1/people/${userId}/medals`),

  settings: () => apiFetch<AppSettings>('/api/v1/admin/settings'),

  saveSettings: (values: Record<string, string | number | boolean>) =>
    apiFetch<AppSettings>('/api/v1/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify({ values }),
    }),

  saveRates: (
    rates: { category_code: string; level: number; rate_percent: number }[],
  ) =>
    apiFetch<AppSettings>('/api/v1/admin/settings/rates', {
      method: 'PUT',
      body: JSON.stringify({ rates }),
    }),

  history: () => apiFetch<HistoryItem[]>('/api/v1/history'),

  communityHistory: () => apiFetch<HistoryItem[]>('/api/v1/admin/history'),

  leave: (id: number) =>
    apiFetch<Registration>(`/api/v1/events/${id}/join`, { method: 'DELETE' }),

  myEvents: (scope: 'upcoming' | 'past' = 'upcoming') =>
    apiFetch<EventCard[]>(`/api/v1/my/events?scope=${scope}`),

  people: (q?: string) =>
    apiFetch<PersonCard[]>(`/api/v1/people${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  person: (id: number) => apiFetch<PersonDetail>(`/api/v1/people/${id}`),

  me: () => apiFetch<PersonDetail>('/api/v1/profile/me'),

  updateMe: (patch: Partial<PersonDetail>) =>
    apiFetch<PersonDetail>('/api/v1/profile/me', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // Админское. Скрытая вкладка — это удобство, а не защита: доступ
  // закрыт на бэкенде, и он отдаёт 404, а не 403.
  stats: (period: StatsPeriod) =>
    apiFetch<AdminStats>(`/api/v1/admin/stats?period=${period}`),

  eventRegistrations: (eventId: number) =>
    apiFetch<Participant[]>(`/api/v1/admin/events/${eventId}/registrations`),

  bonuses: () => apiFetch<Bonuses>('/api/v1/bonuses'),

  club: () => apiFetch<Club>('/api/v1/admin/club'),

  addResident: (body: {
    user_id: number;
    niche: string;
    tier_months: number;
    fee_idr?: number;
  }) =>
    apiFetch<Club>('/api/v1/admin/club/residents', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  removeResident: (userId: number) =>
    apiFetch<Club>(`/api/v1/admin/club/residents/${userId}`, { method: 'DELETE' }),

  setRegistrationStatus: (registrationId: number, status: RegistrationStatus) =>
    apiFetch<Participant>(`/api/v1/admin/registrations/${registrationId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
};
